<template>
  <div class="p-5 pt-1">
    <p class="font-semibold text-3xl">
      {{ this.$route.meta.title }}
    </p>
    <div class="mt-6">
      <div class="bg-white rounded-2xl p-6">
        <div class="flex flex-wrap flex-row items-center justify-start">
          <span class="font-semibold text-lg">Filter</span>
          <select-with-search
            class="flex-grow ml-6"
            :options="tugOwnerOptions"
            placeholder="Select tug owner"
            v-model="filter.tug_owner"
          />
          <date-picker
            class="month flex-grow ml-6"
            type="month"
            format="MMM"
            value-type="MM"
            placeholder="Month"
            v-model="filter.month"
          />
          <date-picker
            class="flex-grow ml-6"
            type="year"
            format="YYYY"
            value-type="format"
            placeholder="Years"
            v-model="filter.years"
          />
          <vs-button
            ref="filterButton"
            class="button shadow-secondary ml-6 vs-con-loading__container"
            color="secondary"
            @click="fetchBargePerformanceList"
          >
            Filter
          </vs-button>
        </div>
      </div>
    </div>
    <data-table
      class="mt-6"
      :table-header="bargePerformanceHeaders"
      :table-data="bargePerformanceData"
      :sort-by="filter.sort_by"
      :sort-dir="filter.sort_dir"
      body-height="100vh"
      @sort="sort"
    >
      <template #action="{ row }">
        <div
          class="mr-1 self-center icon-transparent-bg"
          @click="$router.push({ name: 'BargePerformanceDetail', params: { id: row.company_id } })"
        >
          <img :src="require('@/assets/shipo-icon-see.svg')">
        </div>
      </template>
      <template #avg_sailing_time="{ row }">
        {{ row.avg_sailing_time ? (Math.round(row.avg_sailing_time * 10) / 10).toLocaleString('id-ID') : '-' }}
      </template>
      <template #range="{ row }">
        {{ row.range ? (Math.round(row.range * 10) / 10).toLocaleString('id-ID') : '-' }}
      </template>
      <template #avg_kpi="{ row }">
        {{ row.avg_kpi ? row.avg_kpi.toLocaleString('id-ID') : '-' }}
      </template>
    </data-table>
  </div>
</template>

<script>
import { mapGetters } from 'vuex'

import DataTable from '@/components/dataTable'
import DatePicker from '@/components/datePicker'
import SelectWithSearch from '@/components/selectWithSearch'
import { PAGE_TITLE } from '@/fixtures/Constants'

export default {
  metaInfo() {
    return {
      title: `${PAGE_TITLE} ${this.$route.meta.title}`
    }
  },
  components: {
    DataTable,
    DatePicker,
    SelectWithSearch
  },
  data() {
    return {
      isLoading: false,
      filter: {
        tug_owner: '',
        month: '',
        years: '',
        sort_by: '',
        sort_dir: ''
      },
      tugOwnerOptions: [],
      bargePerformanceHeaders: [
        { key: 'action', label: 'Action' },
        { key: 'rank', label: 'Ranking', sortable: true },
        { key: 'tug_owner', label: 'Tug Owner', sortable: true },
        { key: 'avg_sailing_time', label: 'Average Tug Sailing Time', sortable: true },
        { key: 'avg_kpi', label: 'Average KPI', sortable: true },
        { key: 'range', label: 'Range', sortable: true }
      ],
      bargePerformanceData: []
    }
  },
  computed: {
    ...mapGetters({
      bargePerformances: 'BargePerformance/bargePerformances',
      tugOwners: 'BargePerformance/tugOwners'
    })
  },
  watch: {
    bargePerformances: {
      handler(val) {
        if (val) {
          this.bargePerformanceData = val
        }
      }
    },
    tugOwners: {
      handler(val) {
        if (!this.isEmpty(val)) {
          this.tugOwnerOptions = val.map(item => ({ text: item.tug_owner, value: item.tug_owner }))
        }
      }
    }
  },
  mounted() {
    this.fetchBargePerformanceList()
    this.fetchTugOwners()
  },
  methods: {
    async fetchTugOwners() {
      try {
        await this.$store.dispatch('BargePerformance/fetchTugOwners')
      } catch (e) {
        this.showFailedNotification(this, 'Failed to fetch Tug Owner list, please try again')
      }
    },
    async fetchBargePerformanceList() {
      try {
        if (!!(!!this.filter.month ^ !!this.filter.years)) {
          this.showWarningNotification(this, 'Please fill out both month and year fields')
          return
        }

        this.isLoading = true
        this.changeButtonState(this.isLoading, this.$refs.filterButton.$el)
        await this.$store.dispatch('BargePerformance/fetchBargePerformances',
          Object.keys(this.filter)
            .filter(key => !!this.filter[key])
            .reduce((item, key) => {
              item[key] = this.filter[key]
              return item
            }, {})
        )

        this.isLoading = false
        this.changeButtonState(this.isLoading, this.$refs.filterButton.$el)
      } catch (e) {
        this.isLoading = false
        this.changeButtonState(this.isLoading, this.$refs.filterButton.$el)
        this.showFailedNotification(this, 'Failed to fetch Barge Performance list, please try again')
      }
    },
    sort(sortBy, sortDir) {
      this.filter.sort_by = sortBy
      this.filter.sort_dir = sortDir

      this.fetchBargePerformanceList()
    },
    changeButtonState(state, container) {
      if (state) {
        this.$vs.loading({
          background: 'secondary',
          color: '#FFF',
          container,
          scale: 0.35
        })
        return
      }

      this.$vs.loading.close(container)
    }
  }
}
</script>

<style lang="scss" scoped>
.button {
  @apply py-4 #{!important};
  @apply rounded-lg;
  @apply border-2;
  @apply font-semibold;
  @apply min-w-button;
  @apply text-base;
  height: calc( 1rem * 10 / 3 );

  &.shadow-secondary {
    -webkit-box-shadow: 0 8px 25px -8px rgba(var(--vs-secondary),1);
    box-shadow: 0 8px 25px -8px rgba(var(--vs-secondary),1);
  }

  &:focus {
    @apply outline-none;
  }
}

.icon-transparent-bg {
  @apply cursor-pointer;
  @apply rounded-lg;
  @apply w-8;
  @apply h-8;
  background-color: rgba(252, 219, 208, 0.36);
}

/deep/.select-container {
  @apply w-form-field #{!important};
}
</style>
